import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Breadcrumbs,
  Link,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { Spinner, CustomizedSnackbars } from "../../../components";

// Import components, hooks and utils
import SupplierForm from "../SupplierComponent/SupplierForm";
import { useSupplierData } from "../hooks/useSupplierData";
import { validateSupplierForm } from "../utils/validation";

function AddSupplier() {
  const navigate = useNavigate();

  // Use custom hook for API functions
  const { addSupplier } = useSupplierData();

  // Form state
  const [formData, setFormData] = useState({
    supplierName: "",
    supplierContacName: "",
    supplierPhone: "",
    supplierEmail: "",
    supplierAddress: "",
  });
  const [errors, setErrors] = useState({});

  // Loading and alert states
  const [isSpinner, setIsSpinner] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [alertType, setAlertType] = useState("");
  const [message, setMessage] = useState("");

  // Handle form submission
  const handleSubmit = async () => {
    // Validate form
    const validationErrors = validateSupplierForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSpinner(true);

    try {
      const result = await addSupplier(formData);

      if (result.success) {
        showSuccess("Supplier added successfully");

        // Navigate back to suppliers list after short delay
        setTimeout(() => {
          navigate("/supplier");
        }, 300);
      } else {
        showError(result.error || "Failed to add supplier");
      }
    } catch (error) {
      showError(error.message || "An error occurred");
    } finally {
      setIsSpinner(false);
    }
  };

  // Utility functions
  const showSuccess = (msg) => {
    setIsOpen(true);
    setAlertType("success");
    setMessage(msg);
  };

  const showError = (msg) => {
    setIsOpen(true);
    setAlertType("error");
    setMessage(msg);
  };

  return (
    <>
      <Spinner isShowSpinner={isSpinner} />
      <CustomizedSnackbars
        isOpen={isOpen}
        typeInfo={alertType}
        messageInfo={message}
        handleCloseSnackbar={() => setIsOpen(false)}
      />

      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <Link
                color="inherit"
                onClick={() => navigate("/supplier")}
                sx={{ cursor: "pointer", textDecoration: "none" }}
              >
                Suppliers
              </Link>
              <Typography color="text.primary">Add New Supplier</Typography>
            </Breadcrumbs>

            <Typography variant="h6" sx={{ mt: 1 }}>
              Add New Supplier
            </Typography>
          </Box>
        </Box>

        {/* Form Content */}
        <SupplierForm
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          setErrors={setErrors}
        />

        {/* Action Buttons */}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/supplier")}
          >
            Back to Suppliers
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
          >
            Save Supplier
          </Button>
        </Box>
      </Paper>
    </>
  );
}

export default AddSupplier;
